// Server-side pagination mixin for Vuetify v-data-table.
//
// Contract: the consuming component must implement a `fetchPage(params)` method
// that returns an axios-like promise. `params` contains:
//   page   - 0-based page index (the API is 0-indexed)
//   size   - items per page
//   search - trimmed search term (only present when non-empty)
//
// The API response must resolve to `{ data: { items, totalItems, totalPages, currentPage } }`.
//
// Usage:
//   mixins: [createPaginationMixin({ debounceMs: 500 })],
//   methods: {
//     fetchPage(params) {
//       return PatientRepository.get(params)
//     },
//   },
//
// The template should use `rows.items` for the table body and the mixin
// computed (`rowsRangeStart`, `rowsRangeEnd`) plus `rows.totalPages` for the
// footer. Bind v-pagination with `v-model="page"` and `@input="onPageChange"`,
// and the items-per-page select with `@change="onItemsPerPageChange"`.

export function createPaginationMixin(options = {}) {
  const { itemsPerPage = 10, pageVisible = 5, debounceMs = 500 } = options

  return {
    data: () => ({
      rows: { items: [], totalItems: 0, totalPages: 0, currentPage: 0 },
      page: 1,
      itemsPerPage,
      pageVisible,
      debounceMs,
      searchTimer: null,
    }),

    computed: {
      rowsRangeStart: function () {
        if (!this.rows.totalItems) return 0
        return this.rows.currentPage * this.itemsPerPage + 1
      },
      rowsRangeEnd: function () {
        if (!this.rows.totalItems) return 0
        return Math.min(
          (this.rows.currentPage + 1) * this.itemsPerPage,
          this.rows.totalItems
        )
      },
    },

    watch: {
      search: function () {
        this.onSearchInput()
      },
    },

    mounted: function () {
      this.loadData()
    },

    beforeDestroy: function () {
      clearTimeout(this.searchTimer)
    },

    methods: {
      getPaginationParams: function () {
        const params = { page: this.page - 1, size: this.itemsPerPage }
        const term = (this.search || '').trim()
        if (term) params.search = term
        return params
      },

      onSearchInput: function () {
        clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => {
          this.page = 1
          this.loadData()
        }, this.debounceMs)
      },

      onPageChange: function () {
        this.loadData()
      },

      onItemsPerPageChange: function () {
        this.page = 1
        this.loadData()
      },

      loadData: async function () {
        this.$eventBus.emit('loading-page')
        try {
          const res = await this.fetchPage(this.getPaginationParams())
          const data = res.data?.data || {}
          this.rows = {
            items: data.items || [],
            totalItems: data.totalItems ?? 0,
            totalPages: data.totalPages ?? 0,
            currentPage: data.currentPage ?? 0,
          }
          // Keep the UI page in sync with the served page (the server may clamp it).
          if (this.page !== this.rows.currentPage + 1) {
            this.page = this.rows.currentPage + 1
          }
        } catch (error) {
          if (error.response?.status === 401) {
            localStorage.removeItem('user')
            location.reload()
          }

          const response = error.response ? error.response.data.message : error

          console.log(response)
        }
      },
    },
  }
}
